/**
 * English strings for the `studio` namespace. The source of truth: French is
 * typed against this file. See `apps/web/src/i18n/README.md`.
 */
const studio = {
  home: {
    loadError: "Couldn't load Studio",
    intro:
      "Cut, reorder and combine recordings, and author chapters by hand. Drafts are yours until you publish.",
    newProject: "New project",
    emptyTitle: "No drafts yet",
    emptyDescription:
      'Start from scratch here, or open any recording and press "Edit in Studio".',
    clipCount_one: "{{count}} clip",
    clipCount_other: "{{count}} clips",
    edited: "Edited {{date}}",
    lastPublish: "Last publish",
    deleteProject: "Delete {{title}}",
  },
  editor: {
    splitNothing: "The playhead is already on a cut — nothing to split.",
    publishedChapters: "Chapters published to the recording.",
    publishedRender: "Publishing — rendering runs in the background.",
    openErrorTitle: "Couldn't open this project",
    openErrorBody:
      "It may have been deleted, or it belongs to someone else — drafts are private to their author.",
    untitled: "Untitled edit",
    rename: "Rename this project",
    unsavedTitle: "Unsaved changes",
    unsaved: "● unsaved",
    fitScreenOff: "Let the editor grow past the screen",
    fitScreenOn: "Fit the whole editor on screen, no scrolling",
    undo: "Undo (Ctrl+Z)",
    redo: "Redo (Ctrl+Shift+Z)",
    saveDraft: "Save draft (Ctrl+S)",
    publish: "Publish",
    split: "Split",
    splitTitle: "Split the clip under the playhead (S)",
    duplicate: "Duplicate",
    duplicateTitle: "Duplicate the selected clip (D)",
    mute: "Mute",
    unmute: "Unmute",
    muteTitle: "Mute the selected clip (M)",
    unmuteTitle: "Unmute the selected clip (M)",
    removeTitle: "Remove the selected clip (Delete)",
    hintTransitions: "Transitions preview approximately — the published render is exact.",
    hintKeys: "Drag clips to reorder · drag edges to trim · S splits · M mutes",
    tabs: {
      sources: "Sources",
      chapters: "Chapters",
    },
    browseLibrary: "Browse library",
    browseLibraryTitle: "Browse the whole library with thumbnails and folders",
    uploadsNote: "Uploads and imports land in the library — browse it to add them here.",
  },
  chapters: {
    defaultTitle: "Chapter {{number}}",
    none: "No chapters yet.",
    count_one: "{{count}} chapter",
    count_other: "{{count}} chapters",
    addAt: "Add at {{time}}",
    emptyHelp:
      "Play the video and add a chapter wherever the topic changes. The first chapter starts at the beginning; each one runs until the next — there is nothing else to line up.",
    removeFirst: "Remove this chapter",
    removeMerge: "Remove — merges into the previous chapter",
    titlePlaceholder: "Chapter title",
    summaryPlaceholder: "One-sentence summary (optional)",
  },
  publish: {
    titleChapters: "Publish chapters",
    titleEdit: "Publish edit",
    chaptersOnlyBody:
      "The video itself is unchanged, so nothing is rendered. Your chapters replace the current ones on the source recording for everyone in the workspace — the previous set stays available under version history.",
    renderBody:
      "This renders a <strong>new recording</strong> from your timeline. The originals are untouched, and rendering runs in the background — a long video takes some minutes.",
    titleLabel: "Title",
    transcriptLegend: "Transcript",
    modes: {
      map: {
        label: "Carry the transcript through the edit",
        detail:
          "Exact and free. Words keep their own timings, cuts remove their words, speaker names survive. The right choice unless the sources had no transcript.",
      },
      asr: {
        label: "Transcribe the published video from scratch",
        detail:
          "Runs speech-to-text on the output. Costs transcription credit and loses manual speaker names — use when sources were untranscribed clips.",
      },
      none: {
        label: "No transcript",
        detail: "Publish silent. Transcription can be run later from the recording page.",
      },
    },
  },
  elements: {
    empty: "Nothing on the timeline yet. Browse the library to add the first video.",
    reorderPart: "Reorder part {{number}}",
    dragToReorder: "Drag to reorder",
    fallbackTitle: "Video",
    namePart: "Name this part",
    muted: "Muted",
    hasTransition: "Enters with a transition",
    from: "from",
    to: "to",
    ofDuration: "of {{duration}}",
  },
  preview: {
    title: "Preview",
    empty: "Add a recording to the timeline to preview it.",
  },
  transitions: {
    crossfade: "Crossfade",
    fadeblack: "Fade to black",
    wipeleft: "Wipe left",
  },
  /** Two-letter badges on the timeline seam and the clip list. */
  transitionsShort: {
    crossfade: "XF",
    fadeblack: "FB",
    wipeleft: "WL",
  },
  timeline: {
    fit: "Fit",
    fitTitle: "Fit the whole timeline in view",
    zoomOut: "Zoom out (Ctrl + scroll)",
    zoomIn: "Zoom in (Ctrl + scroll)",
    clipFallback: "Clip",
    muteClip: "Mute this clip (M)",
    unmuteClip: "Unmute this clip (M)",
    seam: "{{transition}} · {{seconds}}s",
    seamCapped: "{{transition}} · {{seconds}}s (capped here — {{requested}}s requested)",
    addTransition: "Add a transition at this cut",
    transitionInto: "Transition into <name>{{name}}</name>:",
    thisClip: "this clip",
    cut: "Cut",
    for: "for",
    seconds: "{{seconds}}s",
    capped: "Capped by the shorter clip — plays as {{seconds}}s here.",
    capNote: "Long fades are capped at half the shorter clip.",
  },
} as const;

export default studio;
